// MOVE A JOB OR A BATCH BETWEEN MACHINES (#6768, epic #6552 "work between machines"): the
// ONE pure verdict that decides whether a move may happen and what it costs. The write route
// and the preview route both call it (`apps/api/src/deps/box-move.ts`), the MCP `box move`
// action reaches it through that same control, and the dashboard never re-derives it.
//
// Owner ruling (ivndev001, 2026-10-20, collab planning): the full move-work design ships,
// with SERVER-enforced limits. The limits are domain constants, never an env knob:
//
//  - a machine sends or takes at most {@link MOVE_BUDGET_PER_MACHINE} moves per rolling
//    {@link MOVE_WINDOW_MS}; a request costs {@link moveCost} of its jobs, charged to BOTH
//    machines;
//  - each moved job rests {@link MOVE_REST_MS} from the moment its move was accepted;
//  - at most {@link MOVE_TRANSFERS_IN_FLIGHT_PER_ORG} transfers are in flight per org, a
//    transfer being ONE accepted request (one batch) until every item in it settled.
//
// Every figure is DERIVED by the caller from the stored `move_batches` / `move_items` rows
// (`@developerz/db` `queries/moves.ts`); there is no second ledger. A refusal changes nothing:
// the caller writes a batch only on `accept`.
//
// Owner ruling (ivndev001, 2026-11-01, #6768 issuecomment-6553762096): code merges ahead of
// runner releases, gated per box at runtime. A RUNNING job is checkpointed through the pause
// verb, so it moves only off a machine whose own live beat lists `pause` in `slots.verbs`
// (`source.canCheckpoint`, derived by the caller from #6751's `pausePresenceVerdict`, never
// re-derived here). A PAUSED job's move sends nothing to any box and is gated on no verb.
//
// AN EPIC OR A CAPABILITY GROUP (#6769). An item names a task, an epic (a plan id) or a
// capability group (an `AgentModeKey`); the caller reads which tasks it names and this file
// decides what moves ({@link moveSelection}):
//
//  - an epic's slices ON the source are jobs, checked like a named task (one refused slice
//    refuses the whole request); its QUEUED slices (`pending`, on no machine, untargeted) are
//    sent to the target by the `retarget` arm and cost nothing; an epic with neither is
//    `epic_empty`, an unknown or foreign plan `item_gone`;
//  - a group's jobs on the source move when {@link jobRefusal} would not refuse them, and are
//    skipped otherwise; a group with none is `group_empty`;
//  - every task moves once, owned by the first item that names it; a batch moves at most
//    {@link MOVE_BATCH_TASKS_MAX} tasks, the jobs on the source first.

import { z } from 'zod';
import { TaskStatus } from '../task/status';
import { RUNNER_MAX_SLOTS_MAX } from './max-slots';
import type { SLOT_REFUSALS } from './slots';

type SlotRefusal = (typeof SLOT_REFUSALS)[number];

/** Moves a machine sends or takes per rolling {@link MOVE_WINDOW_MS} (move-limits ruling). */
export const MOVE_BUDGET_PER_MACHINE = 4;
/** The rolling window the per-machine budget is counted in. */
export const MOVE_WINDOW_MS = 60_000;
/** How long a moved job rests before it may move again, from the accept. */
export const MOVE_REST_MS = 20_000;
/** Accepted requests (batches) in flight at once per org. */
export const MOVE_TRANSFERS_IN_FLIGHT_PER_ORG = 2;

/**
 * Distinct tasks one batch moves (#6769): a machine's jobs-at-once ceiling. An epic's queued
 * slices past it stay untargeted ({@link moveSelection}). The jobs on the source are never cut:
 * they are bounded by the target's room instead, whose cap is at most this ceiling on every
 * path (`boxSlotCap` takes `min(max_slots, …)` and throws on a `max_slots` past
 * `RUNNER_MAX_SLOTS_MAX`, `runners_max_slots_ck`), so more jobs than this is `no_room`. It keeps
 * the reconcile's bound true (each open batch at most a machine's jobs,
 * `queries/tasks/target.ts`), and lets an Undo, which names the batch's tasks, always fit the
 * request's item cap (`MoveItemsWireSchema`, `@developerz/mcp`).
 */
export const MOVE_BATCH_TASKS_MAX = RUNNER_MAX_SLOTS_MAX;

/**
 * What a move item names: a task, an epic (its plan id) or a capability group (its
 * `AgentModeKey`). The stored closed set's second release (`docs/standards/stored-closed-sets.md`):
 * #6768 declared all three and wrote `task`; #6769 writes `epic` and `group`.
 */
export const MOVE_ITEM_KINDS = ['task', 'epic', 'group'] as const;
export const MoveItemKindSchema = z.enum(MOVE_ITEM_KINDS);
export type MoveItemKind = z.infer<typeof MoveItemKindSchema>;

/** One item's life: `checkpointing` / `moving` are OPEN; `landed` / `failed` are settled. */
export const MOVE_ITEM_STATES = ['checkpointing', 'moving', 'landed', 'failed'] as const;
export const MoveItemStateSchema = z.enum(MOVE_ITEM_STATES);
export type MoveItemState = z.infer<typeof MoveItemStateSchema>;
export const MOVE_OPEN_ITEM_STATES = [
  'checkpointing',
  'moving',
] as const satisfies readonly MoveItemState[];

/**
 * Why an item failed. `source_ended`: the task left the move's path before it landed other
 * than by a cancel or the target window. `target_released`: the ten-minute target window
 * passed unclaimed. `cancelled`: the task was cancelled while the move was open.
 */
export const MOVE_ITEM_FAILURES = ['source_ended', 'target_released', 'cancelled'] as const;
export const MoveItemFailureSchema = z.enum(MOVE_ITEM_FAILURES);
export type MoveItemFailure = z.infer<typeof MoveItemFailureSchema>;

/** A batch is `in_flight` until every item settled, then by its items' outcome. */
export const MOVE_BATCH_STATES = ['in_flight', 'landed', 'failed', 'partial'] as const;
export const MoveBatchStateSchema = z.enum(MOVE_BATCH_STATES);
export type MoveBatchState = z.infer<typeof MoveBatchStateSchema>;

/** Every reason a move is refused; `reason` on the problem+json a client branches on. */
export const MOVE_REFUSAL_REASONS = [
  'item_gone',
  'epic_empty',
  'group_empty',
  'undo_unavailable',
  'source_offline',
  'waiting_on_answer',
  'still_starting',
  'still_moving',
  'lane_not_pausable',
  'stop_requested',
  'cannot_checkpoint',
  'cooling_down',
  'source_budget',
  'transfers_in_flight',
  'already_there',
  'target_offline',
  'capability_off',
  'no_room',
  'other_company_there',
  'repo_already_there',
  'target_budget',
] as const;
export type MoveRefusalReason = (typeof MOVE_REFUSAL_REASONS)[number];

/** Which side of the move a refusal is about. */
export type MoveSide = 'request' | 'source' | 'target' | 'org';

/** One accepted move charged to a machine: when it was accepted and what it cost. */
export type MoveUse = { readonly at: Date; readonly cost: number };

/** What the caller read of one task, under the move's row lock. */
export type MoveTaskFacts = {
  readonly taskId: string;
  readonly status: TaskStatus;
  readonly runnerId: string | null;
  readonly pinnedRunnerId: string | null;
  /** The row's hard target (#6767); a queued slice with one is not this move's to send. */
  readonly targetRunnerId: string | null;
  /** A run was started on the row (`run_id` set). */
  readonly runStarted: boolean;
  /**
   * The job's lane saves its work (`isPausableTask`, read by the caller: importing it here
   * would load the task barrel into every reader of the runner barrel, a cycle drizzle-kit's
   * loader cannot resolve).
   */
  readonly pausable: boolean;
  readonly cancelRequested: boolean;
  readonly pauseRequested: boolean;
  readonly leaseEpoch: number;
  readonly repo: string | null;
  /** An open (`checkpointing` / `moving`) move item names this task. */
  readonly openMove: boolean;
  /** An open blocker on this task waits on a person's answer. */
  readonly waitingOnAnswer: boolean;
  /** The newest accepted move of this task; it rests {@link MOVE_REST_MS} from it. */
  readonly lastMovedAt: Date | null;
  /** The task's role is off on the target (machine, org or repo switch). */
  readonly capabilityOffOnTarget: boolean;
};

/**
 * One requested item, with what the caller read of the tasks it names:
 *  - `task`: null when no task of this account has the id;
 *  - `epic`: the plan's dispatched slices in plan order, null for an unknown or foreign plan;
 *  - `group`: the account's jobs on the source whose mode key is `ref`.
 */
export type MoveItemInput =
  | { readonly kind: 'task'; readonly ref: string; readonly task: MoveTaskFacts | null }
  | {
      readonly kind: 'epic';
      readonly ref: string;
      readonly slices: readonly MoveTaskFacts[] | null;
    }
  | { readonly kind: 'group'; readonly ref: string; readonly jobs: readonly MoveTaskFacts[] };

export type MoveSourceFacts = {
  readonly runnerId: string;
  /** False for an absent beat AND for an unreadable presence: fail closed. */
  readonly online: boolean;
  /** The source's own beat lists `pause` (`pausePresenceVerdict` answered `null`). */
  readonly canCheckpoint: boolean;
};

export type MoveTargetFacts = {
  readonly runnerId: string;
  readonly online: boolean;
  /**
   * The target's room. `occupancy` = held + reserved + pending rows targeted at it, before
   * this request; `cap` = `boxSlotCap`; `accepting` / `refusal` are its beat's `slots`.
   */
  readonly room: {
    readonly accepting: boolean;
    readonly refusal: SlotRefusal | null;
    readonly occupancy: number;
    readonly cap: number;
  };
  /** Whose work the target holds or reserves, and on which repos; null when it holds none. */
  readonly occupiedBy: { readonly accountId: string; readonly repos: readonly string[] } | null;
};

export type MoveVerdictInput = {
  readonly accountId: string;
  readonly items: readonly MoveItemInput[];
  readonly source: MoveSourceFacts;
  readonly target: MoveTargetFacts;
  /** The accepted moves charged to each machine inside the window ending at `now`. */
  readonly window: { readonly source: readonly MoveUse[]; readonly target: readonly MoveUse[] };
  /** The org's batches still `in_flight`. */
  readonly inFlight: number;
  /** `none` for a plain move; an undo's eligibility otherwise (newest batch, all landed). */
  readonly undo: 'none' | 'available' | 'unavailable';
  readonly now: Date;
};

/** A machine's budget: moves left in the window and when the next one frees (null = none used). */
export type MoveBudget = { readonly left: number; readonly freeAt: Date | null };

/**
 * How one accepted item moves: a running job checkpoints, a paused one re-pends at once, and
 * an epic's queued slice is sent to the target where it waits (#6769).
 */
export type MoveArm = 'checkpoint' | 'repend' | 'retarget';

export type MoveAccept = {
  readonly kind: 'accept';
  readonly cost: number;
  /** The jobs on the source this moves now; an epic's queued slices are not among them. */
  readonly jobs: number;
  readonly items: readonly {
    readonly kind: MoveItemKind;
    readonly ref: string;
    readonly taskId: string;
    readonly arm: MoveArm;
    readonly leaseEpoch: number;
  }[];
  /** Each machine's budget AFTER this request is charged. */
  readonly budget: { readonly source: MoveBudget; readonly target: MoveBudget };
};

/** The limit a 429 names: what is left, what was needed, when enough frees (null = no time). */
export type MoveLimit = {
  readonly left: number;
  readonly needs: number;
  readonly freeAt: Date | null;
};

export type MoveRefusal = {
  readonly kind: 'refused';
  readonly status: 409 | 429;
  readonly reason: MoveRefusalReason;
  readonly side: MoveSide;
  readonly machineId: string | null;
  readonly item: string | null;
  readonly limit: MoveLimit | null;
  /** `no_room` only: which room check failed. */
  readonly room?: {
    readonly check: 'slots' | 'not_accepting';
    readonly refusal: SlotRefusal | null;
    readonly occupancy: number;
    readonly cap: number;
    readonly needs: number;
  };
};

export type MoveVerdict = MoveAccept | MoveRefusal;

/** A request's cost: one move per distinct job, at least one and at most the budget. */
export function moveCost(jobs: number): number {
  return Math.min(MOVE_BUDGET_PER_MACHINE, Math.max(1, jobs));
}

/** The uses still inside the window ending at `now`, oldest first. */
function liveUses(uses: readonly MoveUse[], now: Date): MoveUse[] {
  const floor = now.getTime() - MOVE_WINDOW_MS;
  return uses.filter((u) => u.at.getTime() > floor).sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** Moves left on a machine at `now`, and when the oldest live use leaves the window. */
export function machineBudget(uses: readonly MoveUse[], now: Date): MoveBudget {
  const live = liveUses(uses, now);
  const used = live.reduce((n, u) => n + u.cost, 0);
  const oldest = live[0];
  return {
    left: Math.max(0, MOVE_BUDGET_PER_MACHINE - used),
    freeAt: oldest === undefined ? null : new Date(oldest.at.getTime() + MOVE_WINDOW_MS),
  };
}

/** The instant enough of the window frees for `cost`: the oldest uses leave first. */
function budgetFreeAt(uses: readonly MoveUse[], now: Date, cost: number): Date | null {
  const live = liveUses(uses, now);
  let left = MOVE_BUDGET_PER_MACHINE - live.reduce((n, u) => n + u.cost, 0);
  for (const u of live) {
    if (left >= cost) break;
    left += u.cost;
    if (left >= cost) return new Date(u.at.getTime() + MOVE_WINDOW_MS);
  }
  return null;
}

type Refuse = (
  reason: MoveRefusalReason,
  side: MoveSide,
  extra?: { item?: string; limit?: MoveLimit; room?: MoveRefusal['room'] },
) => MoveRefusal;

const STATUS_OF: Partial<Record<MoveRefusalReason, 429>> = {
  cooling_down: 429,
  source_budget: 429,
  transfers_in_flight: 429,
  target_budget: 429,
};

function refuser(input: MoveVerdictInput): Refuse {
  const machineOf = (side: MoveSide) =>
    side === 'source' ? input.source.runnerId : side === 'target' ? input.target.runnerId : null;
  return (reason, side, extra = {}) => ({
    kind: 'refused',
    status: STATUS_OF[reason] ?? 409,
    reason,
    side,
    machineId: machineOf(side),
    item: extra.item ?? null,
    limit: extra.limit ?? null,
    ...(extra.room !== undefined ? { room: extra.room } : {}),
  });
}

/** Is the task ON the source: held there, or paused and pinned there. */
function onSource(t: MoveTaskFacts, source: string): boolean {
  if (t.status === TaskStatus.Paused) return t.pinnedRunnerId === source;
  const held = t.status === TaskStatus.Assigned || t.status === TaskStatus.InProgress;
  return held && t.runnerId === source;
}

/**
 * Is the task an epic slice still QUEUED: `pending`, on no machine, untargeted. Whether it may
 * move is {@link jobRefusal}'s, as for a job on the source (an open move is `still_moving`).
 */
function isQueued(t: MoveTaskFacts): boolean {
  return (
    t.status === TaskStatus.Pending &&
    t.runnerId === null &&
    t.pinnedRunnerId === null &&
    t.targetRunnerId === null
  );
}

/** The request-side refusal of one item, in the table's order: a name that moves nothing. */
function itemRefusal(
  input: MoveVerdictInput,
  it: MoveItemInput,
  refuse: Refuse,
): MoveRefusal | null {
  const at = { item: it.ref };
  const source = input.source.runnerId;
  if (it.kind === 'task') {
    return it.task === null || !onSource(it.task, source)
      ? refuse('item_gone', 'request', at)
      : null;
  }
  if (it.kind === 'epic') {
    if (it.slices === null) return refuse('item_gone', 'request', at);
    const any = it.slices.some((t) => onSource(t, source) || isQueued(t));
    return any ? null : refuse('epic_empty', 'request', at);
  }
  return null;
}

function jobRefusal(input: MoveVerdictInput, t: MoveTaskFacts, refuse: Refuse): MoveRefusal | null {
  const at = { item: t.taskId };
  if (t.waitingOnAnswer) return refuse('waiting_on_answer', 'source', at);
  const running = t.status === TaskStatus.InProgress;
  if (t.status === TaskStatus.Assigned || (running && !t.runStarted)) {
    return refuse('still_starting', 'source', at);
  }
  if (t.openMove) return refuse('still_moving', 'source', at);
  if (running && !t.pausable) return refuse('lane_not_pausable', 'source', at);
  if (t.cancelRequested || t.pauseRequested) return refuse('stop_requested', 'source', at);
  if (running && !input.source.canCheckpoint) return refuse('cannot_checkpoint', 'source', at);
  if (t.lastMovedAt !== null) {
    const restUntil = t.lastMovedAt.getTime() + MOVE_REST_MS;
    if (input.now.getTime() < restUntil) {
      return refuse('cooling_down', 'source', {
        ...at,
        limit: { left: 0, needs: 1, freeAt: new Date(restUntil) },
      });
    }
  }
  return null;
}

/** A machine's budget check for `cost`; null when it has room. */
function budgetRefusal(
  uses: readonly MoveUse[],
  input: MoveVerdictInput,
  cost: number,
  side: 'source' | 'target',
  refuse: Refuse,
): MoveRefusal | null {
  const { left } = machineBudget(uses, input.now);
  if (left >= cost) return null;
  const reason = side === 'source' ? 'source_budget' : 'target_budget';
  return refuse(reason, side, {
    limit: { left, needs: cost, freeAt: budgetFreeAt(uses, input.now, cost) },
  });
}

/** The jobs a request names STRICTLY: a refusal of any one refuses the request. */
function strictJobs(input: MoveVerdictInput): MoveTaskFacts[] {
  const source = input.source.runnerId;
  return input.items.flatMap((it) => {
    if (it.kind === 'task') return it.task !== null && onSource(it.task, source) ? [it.task] : [];
    if (it.kind === 'epic') return (it.slices ?? []).filter((t) => onSource(t, source));
    return [];
  });
}

/** One task the request moves, with the item that named it first and how it moves. */
export type MoveSelected = {
  readonly kind: MoveItemKind;
  readonly ref: string;
  readonly task: MoveTaskFacts;
  readonly arm: MoveArm;
};

/** What a request moves: the jobs on the source now, then the queued slices it sends. */
export type MoveSelection = {
  readonly jobs: readonly MoveSelected[];
  readonly queued: readonly MoveSelected[];
};

/**
 * WHAT A REQUEST MOVES, de-duplicated (#6769). Pure, and total over any input: the verdict
 * refuses first, and the preview reads it whatever the verdict says.
 *
 *  1. jobs, in request order: a named task on the source; an epic's slices on the source; a
 *     group's jobs on the source that {@link jobRefusal} would not refuse (the rest skipped);
 *  2. queued slices of the request's epics, in plan order, sent by the `retarget` arm;
 *  3. each task once, owned by the first item that named it, and at most
 *     {@link MOVE_BATCH_TASKS_MAX} tasks: the queued slices past it stay where they are.
 */
export function moveSelection(input: MoveVerdictInput): MoveSelection {
  const source = input.source.runnerId;
  const refuse = refuser(input);
  const seen = new Set<string>();
  const jobs: MoveSelected[] = [];
  const queued: MoveSelected[] = [];
  const take = (into: MoveSelected[], it: MoveItemInput, task: MoveTaskFacts, arm: MoveArm) => {
    if (seen.has(task.taskId)) return;
    seen.add(task.taskId);
    into.push({ kind: it.kind, ref: it.ref, task, arm });
  };
  const jobArm = (t: MoveTaskFacts): MoveArm =>
    t.status === TaskStatus.Paused ? 'repend' : 'checkpoint';
  for (const it of input.items) {
    const named =
      it.kind === 'task'
        ? it.task === null
          ? []
          : [it.task]
        : it.kind === 'epic'
          ? (it.slices ?? [])
          : it.jobs.filter((t) => jobRefusal(input, t, refuse) === null);
    for (const t of named) if (onSource(t, source)) take(jobs, it, t, jobArm(t));
  }
  for (const it of input.items) {
    if (it.kind !== 'epic') continue;
    for (const t of it.slices ?? []) if (isQueued(t)) take(queued, it, t, 'retarget');
  }
  return { jobs, queued: queued.slice(0, Math.max(0, MOVE_BATCH_TASKS_MAX - jobs.length)) };
}

/**
 * The request, source and org half of the verdict: everything that does not depend on the
 * target. `null` = this side admits the request. The preview reads it once per request.
 */
export function moveSourceVerdict(input: MoveVerdictInput): MoveRefusal | null {
  const refuse = refuser(input);
  if (input.undo === 'unavailable') return refuse('undo_unavailable', 'request');
  for (const it of input.items) {
    const r = itemRefusal(input, it, refuse);
    if (r) return r;
  }
  if (!input.source.online) return refuse('source_offline', 'source');
  for (const t of strictJobs(input)) {
    const r = jobRefusal(input, t, refuse);
    if (r) return r;
  }
  const { jobs, queued } = moveSelection(input);
  // A SELECTED queued slice passes the same per-task checks (review 4436526003): its rest, an
  // open move, a stop or an open question refuse the whole epic, as a slice on the source
  // does. The checks that need a run (`still_starting`, `lane_not_pausable`,
  // `cannot_checkpoint`) cannot fire on a pending row. A slice past the cap is not moved, so
  // it refuses nothing.
  for (const q of queued) {
    const r = jobRefusal(input, q.task, refuse);
    if (r) return r;
  }
  for (const it of input.items) {
    if (it.kind !== 'group') continue;
    const moves = it.jobs.some((t) => jobs.some((j) => j.task.taskId === t.taskId));
    if (!moves) return refuse('group_empty', 'source', { item: it.ref });
  }
  const cost = moveCost(jobs.length);
  const budget = budgetRefusal(input.window.source, input, cost, 'source', refuse);
  if (budget) return budget;
  if (input.inFlight >= MOVE_TRANSFERS_IN_FLIGHT_PER_ORG) {
    return refuse('transfers_in_flight', 'org', { limit: { left: 0, needs: 1, freeAt: null } });
  }
  return null;
}

/**
 * The target half: `null` = this target admits the request. Assumes the source half passed.
 * It judges the jobs that move NOW; an epic's queued slices take no room, cost nothing and
 * hold no repo until the target claims them (one it cannot claim fails `target_released`).
 */
export function moveTargetVerdict(input: MoveVerdictInput): MoveRefusal | null {
  const refuse = refuser(input);
  const { target } = input;
  if (target.runnerId === input.source.runnerId) return refuse('already_there', 'target');
  if (!target.online) return refuse('target_offline', 'target');
  const jobs = moveSelection(input).jobs.map((j) => j.task);
  for (const t of jobs) {
    if (t.capabilityOffOnTarget) return refuse('capability_off', 'target', { item: t.taskId });
  }
  const { room } = target;
  const roomFacts = {
    refusal: room.refusal,
    occupancy: room.occupancy,
    cap: room.cap,
    needs: jobs.length,
  };
  if (!room.accepting)
    return refuse('no_room', 'target', { room: { check: 'not_accepting', ...roomFacts } });
  if (room.occupancy + jobs.length > room.cap) {
    return refuse('no_room', 'target', { room: { check: 'slots', ...roomFacts } });
  }
  const there = target.occupiedBy;
  if (there !== null && there.accountId !== input.accountId)
    return refuse('other_company_there', 'target');
  // A repo already there, or a second job of this batch on one repo: one job per repo per box.
  const repos = new Set(there?.repos ?? []);
  for (const t of jobs) {
    if (t.repo === null) continue;
    if (repos.has(t.repo)) return refuse('repo_already_there', 'target', { item: t.taskId });
    repos.add(t.repo);
  }
  return budgetRefusal(input.window.target, input, moveCost(jobs.length), 'target', refuse);
}

/** The verdict: refused with the first failing check, or accepted with the charged budgets. */
export function moveVerdict(input: MoveVerdictInput): MoveVerdict {
  const refused = moveSourceVerdict(input) ?? moveTargetVerdict(input);
  if (refused) return refused;
  const { jobs, queued } = moveSelection(input);
  const cost = moveCost(jobs.length);
  const charged = (uses: readonly MoveUse[]) =>
    machineBudget([...uses, { at: input.now, cost }], input.now);
  return {
    kind: 'accept',
    cost,
    jobs: jobs.length,
    items: [...jobs, ...queued].map((s) => ({
      kind: s.kind,
      ref: s.ref,
      taskId: s.task.taskId,
      arm: s.arm,
      leaseEpoch: s.task.leaseEpoch,
    })),
    budget: { source: charged(input.window.source), target: charged(input.window.target) },
  };
}

/** A projection `{before, after}` from REAL readings only: null when any reading is unknown. */
export function moveProjection(
  before: number | null | undefined,
  deltas: readonly (number | null | undefined)[],
): { before: number; after: number } | null {
  if (before === null || before === undefined) return null;
  let after = before;
  for (const d of deltas) {
    if (d === null || d === undefined) return null;
    after += d;
  }
  return { before, after };
}
